import React, { useEffect, useMemo, useRef, useState } from 'react';
import './bookSession.scss';
import SaralLogo from '@/components/Icons/icons/SaralLogo';
import { CS_OFFBOARDING_MEETING_LINK } from '@Saral-Influencer-Marketing/saral-utils/src/constants/hubspot';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import LeftArrow from '../../Icons/icons/LeftArrow';

const BookSession = ({
  title = 'Schedule a Strategy Session with us',
  subTitle = "Get personalized guidance for your influencer marketing program. Choose a time slot below, and let's boost your success together!",
  onBackClick,
  footer = <></>,
  onSubmit,
  defaultLink = CS_OFFBOARDING_MEETING_LINK,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { userDetails } = useSelector((s) => s.user);

  const hasCalledOnSubmit = useRef(false);

  const preFill = useMemo(() => {
    let name = '';

    if (userDetails?.first_name?.length || userDetails?.last_name?.length) {
      name = `${userDetails.first_name} ${userDetails.last_name}`;
    }

    return { name, email: userDetails.email };
  }, [userDetails]);

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(`/dashboard${search}`);
    }
  };

  useCalendlyEventListener({
    onEventScheduled: onSubmit
      ? async (e) => {
          if (hasCalledOnSubmit.current) return;
          hasCalledOnSubmit.current = true;
          try {
            await onSubmit({
              source: 'calendly_event_scheduled',
              event: e,
              payload: e.data?.payload,
              userDetails,
            });
          } catch (error) {
            hasCalledOnSubmit.current = false;
          }
        }
      : undefined,
  });

  useEffect(() => {
    if (!onSubmit) return;

    const handleMessage = async (event) => {
      if (event.origin.includes('hubspot.com')) {
        if (hasCalledOnSubmit.current) return;
        hasCalledOnSubmit.current = true;
        try {
          await onSubmit({
            source: 'hubspot_message',
            event,
            userDetails,
          });
        } catch (error) {
          hasCalledOnSubmit.current = false;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onSubmit, userDetails]);

  useEffect(() => {
    if (!onSubmit) return;

    const urlParams = new URLSearchParams(window.location.search);
    const meetingBooked = urlParams.get('meeting_booked');
    const hubspotMeetingId = urlParams.get('hubspot_meeting_id');
    const eventType = urlParams.get('event_type');
    const inviteeEmail = urlParams.get('invitee_email');

    if (meetingBooked || hubspotMeetingId || eventType || inviteeEmail) {
      if (hasCalledOnSubmit.current) return;
      hasCalledOnSubmit.current = true;
      const handleRedirectBooking = async () => {
        try {
          const source = meetingBooked || hubspotMeetingId ? 'hubspot_redirect' : 'calendly_redirect';
          await onSubmit({
            source,
            urlParams: {
              meeting_booked: meetingBooked,
              hubspot_meeting_id: hubspotMeetingId,
              event_type: eventType,
              invitee_email: inviteeEmail,
            },
            userDetails,
          });
        } catch (error) {
          hasCalledOnSubmit.current = false;
        }
      };
      handleRedirectBooking();
    }
  }, [onSubmit, userDetails]);

  return (
    <div className="strategy-booking-container">
      <div className="strategy-booking-header">
        <div className="back-button" onClick={handleBackClick}>
          <LeftArrow /> Go Back
        </div>
        <div className="title">{title}</div>
        <SaralLogo />
      </div>
      <div className="title-container">
        <div className="sub-title">{subTitle}</div>
      </div>
      <InlineWidget prefill={preFill} styles={{ height: '800px' }} url={defaultLink} />
      <div className="footer-container">{footer}</div>
    </div>
  );
};

export default BookSession;